import {
  ConsultProposalResponseDTO,
  ProposalDetailsResponseDTO
} from "@repo/core/services/entities/Proposals";
import { GenderEnum, LimitReassessmentEnum } from "@repo/core/enums/proposals";
import {
  SecurityDataFormType,
  PersonalDataFormType,
  AddressDataFormType,
  ProfessionDataFormType,
  FidelityProposalFormType
} from "@repo/core/stores/proposalForm/types";
import { pickFirstValidErpDate } from "@repo/core/utils/erpDate.utils";

/**
 * Converts dd/MM/yyyy date string to ISO format
 * Returns empty string if date is invalid
 */
export const convertDateToISO = (dateString?: string | null): string => {
  if (!dateString || dateString.trim() === "") {
    return "";
  }

  try {
    // Parse dd/MM/yyyy format
    const parts = dateString.split("/");
    if (parts.length !== 3) {
      return "";
    }

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const year = parseInt(parts[2], 10);

    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) {
      return "";
    }

    return date.toISOString();
  } catch {
    return "";
  }
};

/**
 * Converts ISO date string to dd/MM/yyyy format
 * Returns empty string if date is invalid or "0001-01-01T00:00:00"
 * Parses the ISO string directly to avoid timezone issues
 */
export const formatDateFromISO = (dateString?: string | null): string => {
  if (
    !dateString ||
    dateString === "0001-01-01T00:00:00" ||
    dateString.trim() === ""
  ) {
    return "";
  }

  try {
    // Parse ISO string directly to avoid timezone conversion issues
    // Format: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
    const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!dateMatch) {
      return "";
    }

    const year = parseInt(dateMatch[1], 10);
    const month = parseInt(dateMatch[2], 10);
    const day = parseInt(dateMatch[3], 10);

    // Validate date values
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return "";
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return "";
    }

    return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
  } catch {
    return "";
  }
};

const resolveAdmissionDateIso = (
  proposal: Partial<ConsultProposalResponseDTO>
): string | undefined => pickFirstValidErpDate(proposal.strTrabalhaDesde);

/**
 * Maps gender string to GenderEnum
 * Returns null if empty or invalid
 */
export const mapGenderFromString = (
  gender?: string | null
): GenderEnum | null => {
  if (!gender || gender.trim() === "") return null;
  if (gender === "M" || gender.toUpperCase() === "M") return GenderEnum.MALE;
  if (gender === "F" || gender.toUpperCase() === "F") return GenderEnum.FEMALE;
  return null;
};

/**
 * Maps ConsultProposalResponseDTO to SecurityDataFormType
 */
export const mapToSecurityDataForm = (
  proposal: Partial<ConsultProposalResponseDTO>
): SecurityDataFormType => {
  const cellPhone =
    proposal.strCelularDDD && proposal.strCelular
      ? `${proposal.strCelularDDD}${proposal.strCelular}`
      : "";

  return {
    cpf: proposal.strCpf || "",
    fullName: proposal.strNome || "",
    cellPhone
  };
};

/**
 * Maps ConsultProposalResponseDTO to PersonalDataFormType
 */
export const mapToPersonalDataForm = (
  proposal: Partial<ConsultProposalResponseDTO>
): PersonalDataFormType => {
  return {
    birthday: formatDateFromISO(proposal.datDataNascimento),
    gender: mapGenderFromString(proposal.strSexo) as GenderEnum,
    martialStatus: proposal.strEstadoCivil || "",
    mothersName: proposal.strNomeMae || "",
    fathersName: proposal.strNomePai || ""
  };
};

/**
 * Maps ConsultProposalResponseDTO to AddressDataFormType
 */
export const mapToAddressDataForm = (
  proposal: Partial<ConsultProposalResponseDTO>
): AddressDataFormType => {
  return {
    zipCode: proposal.strCep || "",
    addressLine1: proposal.strEndereco || "",
    addressLine2: proposal.strComplemento || "",
    number: proposal.strNumero || "",
    neighborhood: proposal.strBairro || "",
    city: proposal.strCidade || "",
    state: proposal.strUf || ""
  };
};

/**
 * Maps ConsultProposalResponseDTO to ProfessionDataFormType
 */
export const mapToProfessionDataForm = (
  proposal: Partial<ConsultProposalResponseDTO>
): ProfessionDataFormType => {
  return {
    professionalCategory: proposal.strClasseProfissional || "",
    profession: proposal.strProfissao || "",
    admissionDate: formatDateFromISO(resolveAdmissionDateIso(proposal)),
    income: proposal.dblRenda ? proposal.dblRenda.toString() : "0",
    professionalClassDescription: proposal.strClasseProfissional || "",
    professionName: proposal.strProfissao || ""
  };
};

/**
 * Maps ConsultProposalResponseDTO to FidelityProposalFormType
 */
export const mapToFidelityProposalForm = (
  proposal: Partial<ConsultProposalResponseDTO>
): FidelityProposalFormType => {
  return {
    birthday: formatDateFromISO(proposal.datDataNascimento),
    martialStatus: proposal.strEstadoCivil || "",
    profession: proposal.strProfissao || "",
    gender: proposal.strSexo || ""
  };
};

/**
 * Maps ProposalDetailsResponseDTO to ConsultProposalResponseDTO
 * Transforms the new API response format to the expected format used throughout the application
 * Only includes fields that are present in the consultation response or will be filled by forms
 */
export const mapProposalDetailsToConsultProposal = (
  details: ProposalDetailsResponseDTO
): Partial<ConsultProposalResponseDTO> => {
  // Convert datDataProposta from dd/MM/yyyy to ISO format
  const datDataPropostaISO = convertDateToISO(details.datDataProposta);

  // Only include fields that exist in the consultation response or will be used in forms
  return {
    lngProposta: details.lngProposta,
    strNome: details.strNome || "",
    strCpf: details.strCpf || "",
    strCliente: details.strCliente || "",
    strProduto: details.strProduto || "",
    strProdutoDescricao: details.strProdutoDescricao || "",
    strSituacao: details.strSituacao || "",
    datDataProposta: datDataPropostaISO,
    strPontoAtendimentoDescricao: details.strPontoAtendimentoDescricao || "",
    strTokenValidado: details.strTokenValidado || "",
    strFoneEnvioToken: details.strFoneEnvioToken || "",
    strDDDEnvioToken: details.strDDDEnvioToken || "",
    strReavaliacaoLimite:
      details.strReavaliacaoLimite || LimitReassessmentEnum.ANALYSIS,
    lstDocumentos: details.lstDocumentos || [],
    // Fields that will be filled by forms (initialized as empty)
    strEstadoCivil: "",
    strSexo: "",
    strProfissao: "",
    datDataNascimento: ""
  };
};
